import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDocumentTitle } from '../../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../../navigation/reverseDocumentTitle'
import { useCurrentSociety } from '../../../../../hooks/useCurrentSociety'
import { useWindowDimensions } from '../../../../../hooks/useWindowDimensions'
import { ticketQueries, ticketSetChatRead } from '../../../../../api/ticket'
import { TicketChatBoard } from '../TicketChat/TicketChat'
import { ErrorView } from '../../../../error/ErrorView'
import { IllustrationChoices } from '../../../../../components/common/Illustration'
import { TicketSideBar } from '../SideBar/TicketSideBar'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { theme } from '../../../../../theme/theme'

export const TicketDetailViewBoard = (): React.ReactNode => {
  const { society } = useCurrentSociety()
  const { ticketId } = useParams()
  const { width } = useWindowDimensions()
  const canShowSidebarAlways = width >= theme.screen.xl
  const [isSideBarOpen, setIsSideBarOpen] = useState(false)
  const { data: ticket, isLoading } = useQuery(
    ticketQueries.getTicketAsBoard(ticketId, society?._id)
  )

  useDocumentTitle(
    reverseDocumentTitle('management:ticket-detail', {
      '{{ societyName }}': society?.name,
    })
  )

  const client = useQueryClient()
  const { mutate: mutateTicketSetChatRead } = useMutation({
    mutationFn: ticketSetChatRead,
    meta: { skipGlobalInvalidate: true },
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: [{ scope: 'ticket', entity: 'getUnreadTicketsCount' }],
      })
    },
  })
  const { translate } = useAppTranslation()

  useEffect(() => {
    if (ticket?.unreadMessages && ticket?.unreadMessages > 0 && society?._id) {
      mutateTicketSetChatRead({ societyId: society?._id, ticketId: ticket.id })
    }
  }, [ticket, mutateTicketSetChatRead, society?._id])

  if (ticket) {
    return (
      <div className="flex w-full">
        <TicketChatBoard
          ticket={ticket}
          showSideBarButton={!canShowSidebarAlways}
          onOpenSideBar={() => setIsSideBarOpen(true)}
        />
        <TicketSideBar
          ticket={ticket}
          society={society}
          isOverlay={!canShowSidebarAlways}
          isOpen={canShowSidebarAlways || isSideBarOpen}
          onClose={() => setIsSideBarOpen(false)}
        />
      </div>
    )
  }

  if (isLoading) {
    return null
  }

  return (
    <ErrorView
      illustration={IllustrationChoices.EMPTY}
      title={translate('ticket.error.notFound')}
    />
  )
}
